import {Mutations} from '../../api/queries';
import {useTextInputState} from '../../hooks';
import {Box, Button, Icon, Input, LoaderButton, Modal, Text} from '@sproutsocial/racine';
import {useMutation} from '@tanstack/react-query';
import {type SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {keyBy} from 'shared/src/map';
import type {Product} from 'shared/src/models';

interface ProductEditModalProps {
	product?: Product;
	onClose: (didMakeUpdates: boolean) => void;
}

const ProductEditModal = ({product: existingProduct, onClose}: ProductEditModalProps) => {
	const [name, setName] = useTextInputState(existingProduct?.name ?? '');
	const [variants, _setVariants] = useState<{id: string; description: string; isNew: boolean}[]>(
		() => {
			if (existingProduct?.variants?.length) {
				return [
					...existingProduct?.variants?.map((variant) => ({
						id: variant.id + '',
						description: variant.description,
						isNew: false,
					})),
					{
						id: crypto.randomUUID(),
						description: '',
						isNew: true,
					},
				];
			} else {
				return [
					{
						id: crypto.randomUUID(),
						description: '',
						isNew: true,
					},
				];
			}
		},
	);

	const setVariants = useCallback((newVariants: typeof variants) => {
		// Make sure the last variant is always a "new" variant
		if (newVariants[newVariants.length - 1].description.trim()) {
			newVariants.push({
				id: crypto.randomUUID(),
				description: '',
				isNew: true,
			});
		}

		_setVariants(newVariants);
	}, []);

	const onUpdateVariant = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			const variantId = event.currentTarget.parentElement!.dataset['variantId']!;

			const newVariants = variants.map((variant) => {
				if (variant.id === variantId) {
					return {
						...variant,
						description: event.currentTarget.value,
					};
				} else {
					return variant;
				}
			});

			setVariants(newVariants);
		},
		[variants, setVariants],
	);

	const onRemoveVariant = useCallback(
		(event: SyntheticEvent<HTMLElement>) => {
			const variantId = event.currentTarget.dataset['variantId']!;

			setVariants(variants.filter((variant) => variant.id !== variantId));
		},
		[variants, setVariants],
	);

	const saveEnabled = useMemo(() => {
		if (!name.trim()) {
			return false;
		}

		for (const variant of variants) {
			if (!variant.description.trim() && !variant.isNew) {
				return false;
			}
		}

		return true;
	}, [name, variants]);

	const onCancel = useCallback(() => {
		onClose(false);
	}, [onClose]);

	const {mutateAsync: createProduct, isPending: isCreatingProduct} = useMutation(
		Mutations.createProduct(),
	);
	const {mutateAsync: createVariant, isPending: isCreatingVariant} = useMutation(
		Mutations.createProductVariant(),
	);
	const {mutateAsync: updateProduct, isPending: isUpdatingProduct} = useMutation(
		Mutations.updateProduct(),
	);
	const {mutateAsync: updateVariant, isPending: isUpdatingVariant} = useMutation(
		Mutations.updateProductVariant(),
	);

	const isLoading =
		isCreatingProduct || isCreatingVariant || isUpdatingProduct || isUpdatingVariant;

	const onSave = useCallback(async () => {
		if (existingProduct) {
			if (name.trim() !== existingProduct.name) {
				await updateProduct({
					product: {
						id: existingProduct.id,
						name: name.trim(),
						variants: existingProduct.variants ?? [],
					},
				});
			}

			// Update existing variants if they changed
			if (existingProduct.variants?.length) {
				const existingVariantDescriptions = keyBy(
					existingProduct.variants ?? [],
					(variant) => variant.id,
					(variant) => variant.description,
				);

				await Promise.all(
					variants
						.filter((variant) => !variant.isNew)
						.filter(
							(variant) =>
								variant.description.trim() !== existingVariantDescriptions.get(variant.id),
						)
						.map((variant) =>
							updateVariant({
								productVariant: {
									id: variant.id,
									description: variant.description.trim(),
								},
							}),
						),
				);
			}

			// Create all of the new variants
			await Promise.all(
				variants
					.filter((variant) => variant.isNew && variant.description.trim())
					.map((variant) =>
						createVariant({
							productId: existingProduct.id,
							productVariant: {
								description: variant.description.trim(),
							},
						}),
					),
			);
		} else {
			await createProduct({
				product: {
					name: name.trim(),
					variants: variants
						.filter((variant) => variant.description.trim())
						.map((variant) => ({
							description: variant.description.trim(),
						})),
				},
			});
		}

		onClose(true);
	}, [
		existingProduct,
		onClose,
		name,
		variants,
		createProduct,
		createVariant,
		updateProduct,
		updateVariant,
	]);

	return (
		<Modal isOpen closeButtonLabel='Cancel' onClose={onCancel}>
			<Modal.Header title={existingProduct ? 'Edit Product' : 'New Product'} />
			<Modal.Content>
				<Box mb='space.400'>
					<Text fontSize='200' fontWeight='semibold' color='text.body'>
						Name
					</Text>
					<Input id='name' name='name' value={name} onChange={setName} mt='space.200' />
				</Box>
				<Box>
					<Text fontSize='200' fontWeight='semibold' color='text.body'>
						Variants
					</Text>

					<Box display='flex' flexDirection='column' mt='space.200'>
						{variants.map((variant) => (
							<Input
								id={variant.id}
								name={variant.id}
								value={variant.description}
								mb='space.300'
								elemAfter={
									variant.isNew && (
										<Icon
											name='trash-solid'
											color='button.destructive.background.base'
											cursor='pointer'
											data-variant-id={variant.id}
											onClick={onRemoveVariant}
										/>
									)
								}
								onChange={onUpdateVariant}
								data-variant-id={variant.id}
							/>
						))}
					</Box>
				</Box>
			</Modal.Content>
			<Modal.Footer display='flex' justifyContent='flex-end'>
				<Button mr='space.300' onClick={onCancel}>
					Cancel
				</Button>
				<LoaderButton
					appearance='primary'
					minWidth={75}
					disabled={!saveEnabled}
					isLoading={isLoading}
					onClick={onSave}
				>
					{existingProduct ? 'Save' : 'Create'}
				</LoaderButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ProductEditModal;
